/**
 * ADOBE CONFIDENTIAL
 * __________________
 * Copyright 2024 Adobe
 * All Rights Reserved.
 * __________________
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 */

import { getProductData, getRefinedProduct, config } from '@/pdp/api';

export type Options = {
  anchors?: string[];
  optionsUIDs?: string[];
  preselectFirstOption?: boolean;
  isBundle?: boolean;
  skipTransform?: boolean;
};

export const fetchProductData = async (sku: string, options?: Options) => {
  // Get the default configuration values
  const { anchors: defaultAnchors, optionsUIDs: defaultOptionsUIDs } =
    config.getConfig();

  // Destructure the options object
  const {
    optionsUIDs = defaultOptionsUIDs,
    anchors = defaultAnchors,
    preselectFirstOption,
    isBundle,
    skipTransform,
  } = options ?? {};

  // ❗️ Warning ❗️
  // If we have default selections, but we cannot determine that the product is of type bundle, then the response is going to result in null objects and error(s).
  // Only the `products` object will be populated correctly, the `ProductOption${i}` and `refinedProduct` will be set as null.
  // This does not affect the UI, but the error message(s) can be detected by 3rd party tools that the client might be using.
  // This is accepted as a known behaviour, since a bundle product does not have variants.
  // However, if initialData is passed from the integration layer, then we can determine if the product is a bundle and set the isBundle flag accordingly.
  return !isBundle && optionsUIDs && optionsUIDs.length > 0
    ? await getRefinedProduct(sku, optionsUIDs, anchors, skipTransform)
    : await getProductData(
        sku,
        {
          preselectFirstOption,
          optionsUIDs,
        },
        skipTransform
      );
};
